import {
  PendingItemKind,
  PendingItemsChannel,
  PendingItemsErrorCode,
  PendingItemsHandoffStatus,
  PolicyComplianceErrorCode,
  ReadinessReasonCode,
  ReadinessStatus,
  SignerCapacity,
  type PolicyEquipmentScope,
} from '@hapana/shared/schemas/policy-compliance/policy-compliance-enums'

import type {
  ClientCategoryReadiness,
  ClientPolicyComplianceData,
  ReadinessDimensionRow,
  ReadinessItemRow,
} from '_services/policy-compliance.service'
import type {
  EquipmentReadinessRow,
  PendingItemsHandoffItemRow,
  PendingItemsHandoffRow,
  PendingItemsRecipientRow,
} from '_services/pending-items-handoff.service'
import { formatReportNumber } from '@hapana/shared/web/utils/number-format-utils'
import { getSiteNumberFormatSetting } from '@hapana/shared/web/utils/date-format-utils'

export const POLICY_GATE_KEY = 'policyCompliance.checkIn'

export const POLICY_GATE_PERMISSIONS = {
  viewEligibility: 'wellness__equipment_eligibility__view',
  accessClients: 'access__clients',
  recordPaper: 'clients__policy_forms__record_paper',
} as const

export const BeforeEquipmentMode = {
  WAITING: 'WAITING',
  LEGACY: 'LEGACY',
  HIDDEN: 'HIDDEN',
  MERGED: 'MERGED',
} as const
export type BeforeEquipmentMode = (typeof BeforeEquipmentMode)[keyof typeof BeforeEquipmentMode]

export const resolveBeforeEquipmentMode = (input: {
  gateFlag: boolean | null
  canViewEligibility: boolean
}): BeforeEquipmentMode => {
  if (input.gateFlag === null) {
    return BeforeEquipmentMode.WAITING
  }
  if (!input.gateFlag) {
    return BeforeEquipmentMode.LEGACY
  }
  return input.canViewEligibility ? BeforeEquipmentMode.MERGED : BeforeEquipmentMode.HIDDEN
}

type DimensionSource = Pick<ClientCategoryReadiness, 'age' | 'policy'>

const isDimensionEvaluated = (category: DimensionSource): boolean =>
  category.age.status !== ReadinessStatus.NOT_APPLICABLE ||
  category.policy.status !== ReadinessStatus.NOT_APPLICABLE

export const isPolicyGatingActive = (
  compliance: Pick<ClientPolicyComplianceData, 'categories'> | null | undefined
): boolean => (compliance?.categories ?? []).some(isDimensionEvaluated)

const NON_POLICY_KINDS: ReadonlySet<string> = new Set([
  PendingItemKind.SKIN_TYPE_FORM,
  PendingItemKind.AGE_VERIFICATION,
])

const isPolicyItem = (item: Pick<ReadinessItemRow, 'kind'>): boolean =>
  !NON_POLICY_KINDS.has(item.kind)

export type GateItem = {
  itemKey: string
  title: string
  reasonCode: ReadinessItemRow['reasonCode']
  signerCapacity: SignerCapacity | null
  validUntil: string | null
  categories: (PolicyEquipmentScope | null)[]
}

const collectItems = (
  categories: readonly ClientCategoryReadiness[],
  pick: (category: ClientCategoryReadiness) => readonly ReadinessItemRow[]
): GateItem[] => {
  const byKey = new Map<string, GateItem>()
  for (const category of categories) {
    for (const item of pick(category).filter(isPolicyItem)) {
      const existing = byKey.get(item.itemKey)
      if (existing) {
        if (!existing.categories.includes(category.equipmentCategory)) {
          existing.categories.push(category.equipmentCategory)
        }
        continue
      }
      byKey.set(item.itemKey, {
        itemKey: item.itemKey,
        title: item.title,
        reasonCode: item.reasonCode,
        signerCapacity: item.signerCapacity,
        validUntil: item.validUntil,
        categories: [category.equipmentCategory],
      })
    }
  }
  return [...byKey.values()]
}

export const mergeDimensions = (
  dimensions: readonly ReadinessDimensionRow[]
): ReadinessDimensionRow => {
  const failing = dimensions.filter((dimension) => dimension.status === ReadinessStatus.FAIL)
  if (failing.length > 0) {
    return {
      status: ReadinessStatus.FAIL,
      reasonCodes: [...new Set(failing.flatMap((dimension) => dimension.reasonCodes))],
    }
  }
  return dimensions.some((dimension) => dimension.status === ReadinessStatus.PASS)
    ? { status: ReadinessStatus.PASS, reasonCodes: [] }
    : { status: ReadinessStatus.NOT_APPLICABLE, reasonCodes: [] }
}

export const AgeIssue = {
  PROHIBITED: 'PROHIBITED',
  MEDICAL_UNSUPPORTED: 'MEDICAL_UNSUPPORTED',
  VERIFY: 'VERIFY',
  GUARDIAN_PRESENCE: 'GUARDIAN_PRESENCE',
} as const
export type AgeIssue = (typeof AgeIssue)[keyof typeof AgeIssue]

const AGE_ISSUE_BY_CODE: readonly [string, AgeIssue][] = [
  [ReadinessReasonCode.AGE_VERIFICATION_REQUIRED, AgeIssue.VERIFY],
  [ReadinessReasonCode.AGE_PROHIBITED, AgeIssue.PROHIBITED],
  [ReadinessReasonCode.MEDICAL_AUTHORIZATION_UNSUPPORTED, AgeIssue.MEDICAL_UNSUPPORTED],
  [ReadinessReasonCode.GUARDIAN_PRESENCE_REQUIRED, AgeIssue.GUARDIAN_PRESENCE],
]

export type AgeIssueRow = { issue: AgeIssue; categories: (PolicyEquipmentScope | null)[] }

export const listAgeIssues = (categories: readonly ClientCategoryReadiness[]): AgeIssueRow[] =>
  AGE_ISSUE_BY_CODE.flatMap(([code, issue]) => {
    const matching = categories
      .filter((category) => category.age.reasonCodes.includes(code as ReadinessReasonCode))
      .map((category) => category.equipmentCategory)
    return matching.length > 0 ? [{ issue, categories: matching }] : []
  })

export const GateState = {
  BLOCKED: 'BLOCKED',
  ACTIONABLE: 'ACTIONABLE',
  COMPLETE: 'COMPLETE',
} as const
export type GateState = (typeof GateState)[keyof typeof GateState]

export type BeforeEquipmentSummary = {
  state: GateState
  blockingItems: GateItem[]
  actionableItems: GateItem[]
  skinType: ReadinessDimensionRow
  ageIssues: AgeIssueRow[]
  documentsComplete: boolean
  needsVerifiedDob: boolean
  hasGuardianItems: boolean
  hasOutstandingItems: boolean
  visitRequiredItems: GateItem[]
  dimensions: DimensionInput
}

export const summarizeBeforeEquipment = (
  compliance: Pick<ClientPolicyComplianceData, 'categories'>
): BeforeEquipmentSummary => {
  const categories = compliance.categories
  const blockingItems = collectItems(categories, (category) => category.blockingItems)
  const blockingKeys = new Set(blockingItems.map((item) => item.itemKey))
  const actionableItems = collectItems(categories, (category) => category.actionableItems).filter(
    (item) => !blockingKeys.has(item.itemKey)
  )
  const skinType = mergeDimensions(categories.map((category) => category.skinType))
  const ageIssues = listAgeIssues(categories)
  const skinTypeBlocks = skinType.status === ReadinessStatus.FAIL
  const documentsComplete = blockingItems.length === 0 && !skinTypeBlocks
  const hasAgeBlock = ageIssues.some((row) => row.issue !== AgeIssue.GUARDIAN_PRESENCE)
  const state =
    !documentsComplete || hasAgeBlock
      ? GateState.BLOCKED
      : actionableItems.length > 0
        ? GateState.ACTIONABLE
        : GateState.COMPLETE
  const allItems = [...blockingItems, ...actionableItems]
  return {
    state,
    blockingItems,
    actionableItems,
    skinType,
    ageIssues,
    documentsComplete,
    needsVerifiedDob: ageIssues.some((row) => row.issue === AgeIssue.VERIFY),
    hasGuardianItems: allItems.some((item) => item.signerCapacity === SignerCapacity.GUARDIAN),
    hasOutstandingItems: allItems.length > 0 || skinTypeBlocks,
    visitRequiredItems: [],
    dimensions: {
      age: mergeDimensions(categories.map((category) => category.age)),
      policy: mergeDimensions(categories.map((category) => category.policy)),
      skinType,
      operational: null,
    },
  }
}

type RecipientItems = Pick<PendingItemsRecipientRow, 'items'>

export const applyRecipientItems = (
  summary: BeforeEquipmentSummary,
  recipients: readonly RecipientItems[]
): BeforeEquipmentSummary => {
  const gateKeys = new Set(
    [...summary.blockingItems, ...summary.actionableItems].map((item) => item.itemKey)
  )
  const visitRequired = new Map<string, GateItem>()
  for (const item of recipients.flatMap((recipient) => recipient.items)) {
    if (
      !item.blocking ||
      item.requiredForEquipment !== false ||
      gateKeys.has(item.itemKey) ||
      visitRequired.has(item.itemKey)
    ) {
      continue
    }
    visitRequired.set(item.itemKey, {
      itemKey: item.itemKey,
      title: item.title,
      reasonCode: ReadinessReasonCode.POLICIES_NOT_ACCEPTED,
      signerCapacity: item.signerCapacity,
      validUntil: null,
      categories: [],
    })
  }
  return {
    ...summary,
    visitRequiredItems: [...visitRequired.values()],
    dimensions: { ...summary.dimensions, visitRequiredCount: visitRequired.size },
    hasOutstandingItems:
      summary.hasOutstandingItems || recipients.some((recipient) => recipient.items.length > 0),
  }
}

export const DimensionTone = {
  OK: 'OK',
  ERROR: 'ERROR',
  WARNING: 'WARNING',
  INFO: 'INFO',
  NEUTRAL: 'NEUTRAL',
} as const
export type DimensionTone = (typeof DimensionTone)[keyof typeof DimensionTone]

export const DIMENSION_PILL: Record<DimensionTone, string> = {
  OK: 'paid',
  ERROR: 'failed',
  WARNING: 'requires_action',
  INFO: 'scheduled',
  NEUTRAL: 'staged',
}

export const GateDimension = {
  AGE: 'age',
  POLICY: 'policy',
  SKIN_TYPE: 'skinType',
  OPERATIONAL: 'operational',
} as const
export type GateDimension = (typeof GateDimension)[keyof typeof GateDimension]

export type DimensionInput = {
  age: ReadinessDimensionRow
  policy: ReadinessDimensionRow
  skinType: ReadinessDimensionRow
  operational: ReadinessDimensionRow | null
  visitRequiredCount?: number
}

export type DimensionRow = {
  dimension: GateDimension
  tone: DimensionTone
  valueKey: string
  count?: number
}

const resolveAgeRow = (age: ReadinessDimensionRow): Omit<DimensionRow, 'dimension'> => {
  if (age.status === ReadinessStatus.NOT_APPLICABLE) {
    return { tone: DimensionTone.NEUTRAL, valueKey: 'notApplicable' }
  }
  if (age.status === ReadinessStatus.PASS) {
    return { tone: DimensionTone.OK, valueKey: 'pass' }
  }
  const blocking = age.reasonCodes.filter(
    (code) => code !== ReadinessReasonCode.GUARDIAN_PRESENCE_REQUIRED
  )
  return blocking.length > 0
    ? { tone: DimensionTone.ERROR, valueKey: blocking[0] ?? 'fail' }
    : { tone: DimensionTone.WARNING, valueKey: ReadinessReasonCode.GUARDIAN_PRESENCE_REQUIRED }
}

const resolveSimpleRow = (
  dimension: ReadinessDimensionRow | null,
  empty: Omit<DimensionRow, 'dimension'>
): Omit<DimensionRow, 'dimension'> => {
  if (!dimension || dimension.status === ReadinessStatus.NOT_APPLICABLE) {
    return empty
  }
  if (dimension.status === ReadinessStatus.PASS) {
    return { tone: DimensionTone.OK, valueKey: 'pass' }
  }
  return { tone: DimensionTone.ERROR, valueKey: dimension.reasonCodes[0] ?? 'fail' }
}

const NOT_NEEDED_ROW = { tone: DimensionTone.NEUTRAL, valueKey: 'notApplicable' }
const AT_START_ROW = { tone: DimensionTone.INFO, valueKey: 'atStart' }

const resolvePolicyRow = (input: DimensionInput): Omit<DimensionRow, 'dimension'> => {
  const row = resolveSimpleRow(input.policy, NOT_NEEDED_ROW)
  const visitRequiredCount = input.visitRequiredCount ?? 0
  return row.tone !== DimensionTone.ERROR && visitRequiredCount > 0
    ? { tone: DimensionTone.NEUTRAL, valueKey: 'visitRequired', count: visitRequiredCount }
    : row
}

export const buildDimensionRows = (input: DimensionInput): DimensionRow[] => [
  { dimension: GateDimension.AGE, ...resolveAgeRow(input.age) },
  { dimension: GateDimension.POLICY, ...resolvePolicyRow(input) },
  { dimension: GateDimension.SKIN_TYPE, ...resolveSimpleRow(input.skinType, NOT_NEEDED_ROW) },
  { dimension: GateDimension.OPERATIONAL, ...resolveSimpleRow(input.operational, AT_START_ROW) },
]

export type EquipmentEligibility = {
  blocksStart: boolean
  needsGuardianAttestation: boolean
  items: GateItem[]
  skinType: ReadinessDimensionRow
  ageIssue: AgeIssue | null
}

export const resolveEquipmentEligibility = (
  readiness: EquipmentReadinessRow
): EquipmentEligibility => {
  const items = collectItems([readiness], (entry) => entry.blockingItems)
  const ageIssue = listAgeIssues([readiness])[0]?.issue ?? null
  const needsGuardianAttestation = readiness.age.reasonCodes.includes(
    ReadinessReasonCode.GUARDIAN_PRESENCE_REQUIRED
  )
  const ageBlocks =
    readiness.age.status === ReadinessStatus.FAIL &&
    readiness.age.reasonCodes.some(
      (code) => code !== ReadinessReasonCode.GUARDIAN_PRESENCE_REQUIRED
    )
  return {
    blocksStart: ageBlocks || readiness.policy.status === ReadinessStatus.FAIL,
    needsGuardianAttestation,
    items,
    skinType: readiness.skinType,
    ageIssue: ageBlocks ? ageIssue : needsGuardianAttestation ? AgeIssue.GUARDIAN_PRESENCE : null,
  }
}

export const hasEligibilityContent = (eligibility: EquipmentEligibility): boolean =>
  eligibility.items.length > 0 ||
  eligibility.ageIssue !== null ||
  eligibility.skinType.status === ReadinessStatus.FAIL

export const canStartWithAttestation = (
  eligibility: EquipmentEligibility | null,
  isAttested: boolean
): boolean =>
  !eligibility ||
  (!eligibility.blocksStart && (!eligibility.needsGuardianAttestation || isAttested))

export const WaitlineReason = {
  NOT_ALLOWED: 'NOT_ALLOWED',
  VERIFY_AGE: 'VERIFY_AGE',
  GUARDIAN_CONSENT: 'GUARDIAN_CONSENT',
  NOT_SIGNED: 'NOT_SIGNED',
  GUARDIAN_PRESENCE: 'GUARDIAN_PRESENCE',
} as const
export type WaitlineReason = (typeof WaitlineReason)[keyof typeof WaitlineReason]

export const resolveWaitlineReason = (readiness: EquipmentReadinessRow): WaitlineReason | null => {
  const eligibility = resolveEquipmentEligibility(readiness)
  if (
    eligibility.ageIssue === AgeIssue.PROHIBITED ||
    eligibility.ageIssue === AgeIssue.MEDICAL_UNSUPPORTED
  ) {
    return WaitlineReason.NOT_ALLOWED
  }
  if (eligibility.ageIssue === AgeIssue.VERIFY) {
    return WaitlineReason.VERIFY_AGE
  }
  if (readiness.policy.status === ReadinessStatus.FAIL) {
    return eligibility.items.some((item) => item.signerCapacity === SignerCapacity.GUARDIAN)
      ? WaitlineReason.GUARDIAN_CONSENT
      : WaitlineReason.NOT_SIGNED
  }
  return eligibility.needsGuardianAttestation ? WaitlineReason.GUARDIAN_PRESENCE : null
}

export const CLIENT_RECIPIENT_KEY = 'CLIENT'
const GUARDIAN_RECIPIENT_PREFIX = 'GUARDIAN:'

export type Recipient = {
  key: string
  capacity: SignerCapacity
  guardianClientId: string | null
  name: string
  itemCount: number
}

export const listRecipients = (input: {
  clientName: string
  contacts: readonly (Pick<PendingItemsRecipientRow, 'guardianClientId' | 'name'> & {
    items: readonly Pick<PendingItemsRecipientRow['items'][number], 'signerCapacity'>[]
  })[]
}): Recipient[] => {
  const guardians = input.contacts.flatMap((contact) => {
    const itemCount = contact.items.filter(
      (item) => item.signerCapacity === SignerCapacity.GUARDIAN
    ).length
    return contact.guardianClientId && itemCount > 0
      ? [
          {
            key: `${GUARDIAN_RECIPIENT_PREFIX}${contact.guardianClientId}`,
            capacity: SignerCapacity.GUARDIAN,
            guardianClientId: contact.guardianClientId,
            name: contact.name,
            itemCount,
          },
        ]
      : []
  })
  const clientItems = input.contacts.find((contact) => !contact.guardianClientId)?.items ?? []
  const clientCapacity = clientItems.some(
    (item) => item.signerCapacity === SignerCapacity.PARTICIPANT_MINOR
  )
    ? SignerCapacity.PARTICIPANT_MINOR
    : SignerCapacity.CLIENT_ADULT
  const clientCount = clientItems.filter((item) => item.signerCapacity === clientCapacity).length
  const client =
    clientCount > 0 || guardians.length === 0
      ? [
          {
            key: CLIENT_RECIPIENT_KEY,
            capacity: clientCapacity,
            guardianClientId: null,
            name: input.clientName,
            itemCount: clientCount,
          },
        ]
      : []
  return [...guardians, ...client]
}

export const APP_SEND_CHANNEL = PendingItemsChannel.APP

export const NO_APP_DEVICE_CODE = 'NO_APP_DEVICE'

export const SEND_CHANNELS = [
  PendingItemsChannel.SMS,
  APP_SEND_CHANNEL,
  PendingItemsChannel.QR,
  PendingItemsChannel.KIOSK,
  PendingItemsChannel.DESK,
] as const
export type SendChannel = (typeof SEND_CHANNELS)[number]

export const listSendChannels = (
  contact: Pick<PendingItemsRecipientRow, 'hasAppDevice'> | null
): SendChannel[] =>
  SEND_CHANNELS.filter((entry) => entry !== APP_SEND_CHANNEL || Boolean(contact?.hasAppDevice))

export const resolveDefaultChannel = (needsVerifiedDob: boolean): SendChannel =>
  needsVerifiedDob ? PendingItemsChannel.SMS : PendingItemsChannel.QR

const DOB_VERIFIED_CHANNELS: ReadonlySet<SendChannel> = new Set([
  PendingItemsChannel.QR,
  PendingItemsChannel.KIOSK,
])

export const isChannelBlockedByDob = (channel: SendChannel, needsVerifiedDob: boolean): boolean =>
  needsVerifiedDob && DOB_VERIFIED_CHANNELS.has(channel)

export const findRecipientContact = (
  recipient: Pick<Recipient, 'guardianClientId'> | undefined,
  clientId: string,
  contacts: readonly PendingItemsRecipientRow[] | undefined
): PendingItemsRecipientRow | null => {
  if (!recipient || !contacts) {
    return null
  }
  const signerId = recipient.guardianClientId ?? clientId
  return contacts.find((contact) => contact.signerId === signerId) ?? null
}

export const resolveNeedsVerifiedDob = (
  contact: Pick<PendingItemsRecipientRow, 'dobVerified'> | null,
  fallback: boolean
): boolean => (contact ? !contact.dobVerified : fallback)

export type TextDestinationCopy = { key: string; params: Record<string, string> }

export const describeTextDestination = (
  contact: Pick<PendingItemsRecipientRow, 'maskedMobile' | 'hasValidMobile' | 'name'> | null,
  useOtherNumber: boolean
): TextDestinationCopy | null => {
  if (useOtherNumber || !contact) {
    return null
  }
  return contact.hasValidMobile && contact.maskedMobile
    ? { key: 'send.sms.sendsTo', params: { mobile: contact.maskedMobile, name: contact.name } }
    : { key: 'send.sms.noMobile', params: { name: contact.name } }
}

export const needsTextConsentAttestation = (
  contact: Pick<PendingItemsRecipientRow, 'hasTextConsent'> | null,
  useOtherNumber: boolean
): boolean => useOtherNumber || !contact?.hasTextConsent

const PENDING_TERMINAL: ReadonlySet<string> = new Set([
  PendingItemsHandoffStatus.COMPLETED,
  PendingItemsHandoffStatus.FAILED,
  PendingItemsHandoffStatus.EXPIRED,
  PendingItemsHandoffStatus.CANCELLED,
  PendingItemsHandoffStatus.SUPERSEDED,
  PendingItemsHandoffStatus.LOCKED,
])

export const isPendingHandoffTerminal = (status: string): boolean => PENDING_TERMINAL.has(status)

const SLOW_REFETCH_BASE_MS = 30_000
const SLOW_REFETCH_JITTER_MS = 30_000

export const computeSlowHandoffRefetchMs = (random: number): number =>
  SLOW_REFETCH_BASE_MS + Math.round(Math.min(Math.max(random, 0), 1) * SLOW_REFETCH_JITTER_MS)

export const resolveHandoffRefetchMs = (input: {
  status: string | null | undefined
  isPolling: boolean
  pollDelayMs: number
  slowDelayMs: number
}): number | false => {
  if (!input.status || isPendingHandoffTerminal(input.status)) {
    return false
  }
  return input.isPolling ? input.pollDelayMs : input.slowDelayMs
}

const STEPS_BY_CHANNEL: Record<string, readonly PendingItemsHandoffStatus[]> = {
  [PendingItemsChannel.SMS]: [
    PendingItemsHandoffStatus.SENT,
    PendingItemsHandoffStatus.DELIVERED,
    PendingItemsHandoffStatus.OPENED,
    PendingItemsHandoffStatus.IN_PROGRESS,
    PendingItemsHandoffStatus.COMPLETED,
  ],
  [PendingItemsChannel.DESK]: [
    PendingItemsHandoffStatus.OPENED,
    PendingItemsHandoffStatus.IN_PROGRESS,
    PendingItemsHandoffStatus.COMPLETED,
  ],
  [PendingItemsChannel.KIOSK]: [
    PendingItemsHandoffStatus.SENT,
    PendingItemsHandoffStatus.OPENED,
    PendingItemsHandoffStatus.IN_PROGRESS,
    PendingItemsHandoffStatus.COMPLETED,
  ],
  [APP_SEND_CHANNEL]: [
    PendingItemsHandoffStatus.SENT,
    PendingItemsHandoffStatus.OPENED,
    PendingItemsHandoffStatus.IN_PROGRESS,
    PendingItemsHandoffStatus.COMPLETED,
  ],
}

const DEFAULT_STEPS: readonly PendingItemsHandoffStatus[] = [
  PendingItemsHandoffStatus.CREATED,
  PendingItemsHandoffStatus.OPENED,
  PendingItemsHandoffStatus.IN_PROGRESS,
  PendingItemsHandoffStatus.COMPLETED,
]

export const listPendingTrackerSteps = (channel: string): readonly PendingItemsHandoffStatus[] =>
  STEPS_BY_CHANNEL[channel] ?? DEFAULT_STEPS

export const resolvePendingReachedSteps = (
  handoff: Pick<PendingItemsHandoffRow, 'channel' | 'status'>
): number => {
  const steps = listPendingTrackerSteps(handoff.channel)
  const index = steps.indexOf(handoff.status)
  if (index >= 0) {
    return index + 1
  }
  return handoff.status === PendingItemsHandoffStatus.CREATED ||
    handoff.status === PendingItemsHandoffStatus.SENT
    ? 1
    : 0
}

const PILL_BY_STATUS: Record<PendingItemsHandoffStatus, string> = {
  CREATED: 'scheduled',
  SENT: 'scheduled',
  DELIVERED: 'scheduled',
  OPENED: 'in_use',
  IN_PROGRESS: 'in_use',
  COMPLETED: 'paid',
  FAILED: 'failed',
  EXPIRED: 'failed_canceled',
  CANCELLED: 'canceled',
  SUPERSEDED: 'canceled',
  LOCKED: 'failed',
}

export const resolvePendingTrackerPill = (status: PendingItemsHandoffStatus): string =>
  PILL_BY_STATUS[status]

export const formatCompletionCounts = (completion: {
  done: number
  total: number
}): { done: string; total: string } => {
  const numberFormat = getSiteNumberFormatSetting()
  return {
    done: formatReportNumber(completion.done, numberFormat),
    total: formatReportNumber(completion.total, numberFormat),
  }
}

export const countCompletion = (
  items: readonly Pick<PendingItemsHandoffItemRow, 'countsTowardCompletion' | 'done'>[]
): { done: number; total: number } => {
  const counted = items.filter((item) => item.countsTowardCompletion)
  return { done: counted.filter((item) => item.done).length, total: counted.length }
}

export const listHandedOffItems = (
  items: readonly PendingItemsHandoffItemRow[]
): PendingItemsHandoffItemRow[] => items.filter((item) => !item.countsTowardCompletion)

export const canCancelHandoff = (status: string): boolean => !isPendingHandoffTerminal(status)

export const canResendHandoff = (status: string): boolean =>
  isPendingHandoffTerminal(status) && status !== PendingItemsHandoffStatus.COMPLETED

export const resolveTrackerResultKey = (
  handoff: Pick<PendingItemsHandoffRow, 'status' | 'resultCode'>
): string | null =>
  isPendingHandoffTerminal(handoff.status) &&
  handoff.status !== PendingItemsHandoffStatus.COMPLETED &&
  handoff.resultCode
    ? `tracker.resultCodes.${handoff.resultCode}`
    : null

export const sortHandoffsNewestFirst = (
  handoffs: readonly PendingItemsHandoffRow[]
): PendingItemsHandoffRow[] =>
  [...handoffs].sort((left, right) => Number(right.createdAt) - Number(left.createdAt))

type RequestErrorShape = {
  response?: {
    errors?: ReadonlyArray<{
      extensions?: { code?: string; details?: { reason?: string; failReason?: string } }
    }>
  }
}

const readFirstExtensions = (error: unknown) =>
  (error as RequestErrorShape | null | undefined)?.response?.errors?.[0]?.extensions

const KNOWN_SEND_ERRORS: ReadonlySet<string> = new Set([
  ...Object.values(PendingItemsErrorCode),
  NO_APP_DEVICE_CODE,
])
const KNOWN_SEND_REASONS: ReadonlySet<string> = new Set([
  'SMS_CONSENT_NOT_ATTESTED',
  'NO_VALID_MOBILE',
  'GUARDIAN_IN_PERSON_ONLY',
  'SENDER_NOT_TWO_WAY',
  'INSUFFICIENT_CREDITS',
  NO_APP_DEVICE_CODE,
])

export type SendRefusal = { code: string | null; reason: string | null; copyKey: string }

export const readSendRefusal = (error: unknown): SendRefusal => {
  const extensions = readFirstExtensions(error)
  const code = extensions?.code ?? null
  const reason = extensions?.details?.reason ?? null
  if (reason && KNOWN_SEND_REASONS.has(reason)) {
    return { code, reason, copyKey: `sendErrors.${reason}` }
  }
  return {
    code,
    reason,
    copyKey: `sendErrors.${code && KNOWN_SEND_ERRORS.has(code) ? code : 'generic'}`,
  }
}

const START_REFUSAL_CODES: ReadonlySet<string> = new Set([
  ...Object.values(PolicyComplianceErrorCode),
  ReadinessReasonCode.RENEWAL_EXPIRED,
])

export const readStartRefusalCode = (error: unknown): string | null => {
  const extensions = readFirstExtensions(error)
  const candidates = [extensions?.details?.failReason, extensions?.code]
  return candidates.find((code) => code && START_REFUSAL_CODES.has(code)) ?? null
}

export const resolveStartRefusalKey = (error: unknown): string | null => {
  const code = readStartRefusalCode(error)
  return code ? `${POLICY_GATE_KEY}.startRefusals.${code}` : null
}

export const isPendingItemsSiteEvent = (
  data: { kind?: unknown; clientId?: unknown } | null | undefined
): data is { kind: 'PENDING_ITEMS'; clientId: string; status?: string } =>
  data?.kind === 'PENDING_ITEMS' && typeof data.clientId === 'string'

export const isDocumentsCompleteEvent = (event: { status?: string | null }): boolean =>
  event.status === PendingItemsHandoffStatus.COMPLETED

export type DocumentBanner = {
  kind: GateState
  messageLeaf:
    | 'blocked'
    | 'guardianNeeded'
    | 'skinTypeOnly'
    | 'visitRequired'
    | 'actionable'
    | 'complete'
  count: number
  categories: (PolicyEquipmentScope | null)[]
}

const listItemCategories = (items: readonly GateItem[]): (PolicyEquipmentScope | null)[] => [
  ...new Set(items.flatMap((item) => item.categories)),
]

export const resolveDocumentBannerKind = (summary: BeforeEquipmentSummary): DocumentBanner => {
  const skinTypeBlocks = summary.skinType.status === ReadinessStatus.FAIL
  if (!summary.documentsComplete) {
    const count = summary.blockingItems.length + (skinTypeBlocks ? 1 : 0)
    if (summary.blockingItems.length === 0) {
      return { kind: GateState.BLOCKED, messageLeaf: 'skinTypeOnly', count, categories: [] }
    }
    const needsGuardian = summary.blockingItems.some(
      (item) => item.signerCapacity === SignerCapacity.GUARDIAN
    )
    return {
      kind: GateState.BLOCKED,
      messageLeaf: needsGuardian ? 'guardianNeeded' : 'blocked',
      count,
      categories: listItemCategories(summary.blockingItems),
    }
  }
  if (summary.visitRequiredItems.length > 0) {
    return {
      kind: GateState.ACTIONABLE,
      messageLeaf: 'visitRequired',
      count: summary.visitRequiredItems.length,
      categories: [],
    }
  }
  return summary.actionableItems.length > 0
    ? {
        kind: GateState.ACTIONABLE,
        messageLeaf: 'actionable',
        count: summary.actionableItems.length,
        categories: listItemCategories(summary.actionableItems),
      }
    : { kind: GateState.COMPLETE, messageLeaf: 'complete', count: 0, categories: [] }
}

export const AGE_ISSUE_CODE: Record<AgeIssue, ReadinessReasonCode> = {
  PROHIBITED: ReadinessReasonCode.AGE_PROHIBITED,
  MEDICAL_UNSUPPORTED: ReadinessReasonCode.MEDICAL_AUTHORIZATION_UNSUPPORTED,
  VERIFY: ReadinessReasonCode.AGE_VERIFICATION_REQUIRED,
  GUARDIAN_PRESENCE: ReadinessReasonCode.GUARDIAN_PRESENCE_REQUIRED,
}

const isKnownTimezone = (timezone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

export const resolveSiteTimezone = (
  candidates: readonly (string | null | undefined)[]
): string | null =>
  candidates.find((candidate): candidate is string =>
    Boolean(candidate && isKnownTimezone(candidate))
  ) ?? null
